import { DialogContent, DialogTitle } from '@mui/material'
import { useState } from 'react'
import { useNotify } from 'react-admin'
import { FlexBox, VerticalDivider } from 'shared/components/Base/Base'
import { Button } from 'shared/components/Button/Button'
import { ConfirmDialog } from 'shared/components/Dialog/ConfirmDialog'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { DEFAULT_ERROR_STRING } from 'shared/helpers/constants'
import {
  formatMobile,
  formattedName,
  getTruckNameFromTask,
  getUserName,
  getUserNameFromTask,
} from 'shared/helpers/formatters'
import { InsuranceType, OnboardingChecklistStatusDetail } from 'shared/models/onboarding'
import { enrollIntoOnboardingInsurance } from 'shared/models/team'
import { InsuranceBeneficiary, User } from 'shared/models/user'
import { Colors } from 'shared/theme/colors'
import { STANDARD_WRAPPER_WIDTH } from 'shared/theme/constants'
import styled from 'styled-components'

import { Text } from '../Text/Text'
import { SubTask, Task, TaskStatusEnum } from './TaskList'
import { StyledIcon, StyledMain, StyledStatus, StyledSubTask } from './TaskList.styles'
import { getIconName } from './TaskList.util'

interface InsuranceSubTaskProps {
  tasks: SubTask[]
  isReadOnly?: boolean
  status: TaskStatusEnum
  statusDetail?: OnboardingChecklistStatusDetail
  insuranceType: InsuranceType
  user?: User
}

interface TaskListOnboardingInsuranceProps {
  task: Task
  isReadOnly?: boolean
  user?: User
}

const getStatus = (tasks: Task[]) => {
  if (tasks.every((task) => task.status === TaskStatusEnum.COMPLETED)) {
    return TaskStatusEnum.COMPLETED
  }
  if (tasks.some((task) => task.status === TaskStatusEnum.IN_PROGRESS)) {
    return TaskStatusEnum.IN_PROGRESS
  }
  return TaskStatusEnum.NOT_STARTED
}

const getStatusText = (
  status: TaskStatusEnum,
  insuranceType: InsuranceType,
  statusDetail?: OnboardingChecklistStatusDetail
) => {
  if (status === TaskStatusEnum.COMPLETED) {
    return 'Completed'
  }
  if (status === TaskStatusEnum.IN_PROGRESS) {
    if (insuranceType === InsuranceType.NON_TRUCKING_LIABILITY) {
      return statusDetail === OnboardingChecklistStatusDetail.TEAM_IS_NTL_INSURANCE_OPTED_IN ||
        statusDetail === OnboardingChecklistStatusDetail.TEAM_IS_NTL_AND_OCC_ACC_INSURANCE_OPTED_IN
        ? 'Trucks to enroll with Reliance'
        : 'Insurance doc review needed'
    }
    if (insuranceType === InsuranceType.OCCUPATIONAL_ACCIDENT) {
      return statusDetail === OnboardingChecklistStatusDetail.TEAM_IS_OCC_ACC_INSURANCE_OPTED_IN ||
        statusDetail === OnboardingChecklistStatusDetail.TEAM_IS_NTL_AND_OCC_ACC_INSURANCE_OPTED_IN
        ? 'Drivers to enroll with Reliance'
        : 'Insurance doc review needed'
    }
    return 'In Progress'
  }
  return 'Not Started'
}

const getInsuranceText = (insuranceType: InsuranceType) => {
  if (insuranceType === InsuranceType.NON_TRUCKING_LIABILITY) {
    return 'Non-trucking liability'
  }
  return 'Occupational accident'
}

const InsuranceSubTask = ({
  tasks,
  isReadOnly,
  status,
  statusDetail,
  insuranceType,
  user,
}: InsuranceSubTaskProps) => {
  const notify = useNotify()
  const [currentBeneficiaryInfo, setCurrentBeneficiaryInfo] = useState<InsuranceBeneficiary | undefined>(
    undefined
  )
  const [showBeneficiaryInfo, setShowBeneficiaryInfo] = useState(false)
  const handleViewBeneficiary = (beneficiary: InsuranceBeneficiary) => {
    setCurrentBeneficiaryInfo(beneficiary)
    setShowBeneficiaryInfo(true)
  }

  const handleConfirmEnrollment = async (insuranceType: InsuranceType) => {
    const formData = new FormData()
    formData.append('insurance_types', JSON.stringify([insuranceType]))

    await enrollIntoOnboardingInsurance(user!.team!, formData)
    return Promise.resolve()
  }

  const handleError = (error: any) => {
    notify(`${DEFAULT_ERROR_STRING}: ${JSON.stringify(error?.response?.data || error)}`, {
      type: 'error',
    })
  }

  return (
    <>
      <StyledSubTask>
        <StyledIcon>
          <img src={getIconName({ status })} width="24px" alt="" />
        </StyledIcon>
        <StyledMain>
          <Text variant="header3" fontColor={isReadOnly ? Colors.lightGray1 : undefined}>
            {getInsuranceText(insuranceType)}
          </Text>
        </StyledMain>
        <StyledStatus>
          <Text variant="paragraph2" fontColor={Colors.lightGray1} fontWeight={400}>
            {getStatusText(status, insuranceType, statusDetail)}
          </Text>
          {tasks
            .filter((task) => task.status === TaskStatusEnum.IN_PROGRESS)
            .map((task) => {
              const buttonText =
                insuranceType === InsuranceType.OCCUPATIONAL_ACCIDENT
                  ? getUserNameFromTask(task)
                  : getTruckNameFromTask(task)
              return (
                <FlexBox alignItems="center">
                  <Button
                    variant="text"
                    size="medium"
                    align="left"
                    onClick={() => {
                      task.onCtaClick?.()
                    }}
                  >
                    {buttonText}
                  </Button>

                  {insuranceType === InsuranceType.OCCUPATIONAL_ACCIDENT && (
                    <>
                      <VerticalDivider height="12px" margin="0 12px" />
                      <Button
                        variant="text"
                        size="medium"
                        align="left"
                        onClick={() => handleViewBeneficiary(task.context?.user?.insuranceBeneficiary!)}
                      >
                        View beneficiary
                      </Button>
                    </>
                  )}

                  {user?.featureFlags?.publicOnboardingInsuranceEnrollment && (
                    <>
                      <VerticalDivider height="12px" margin="0 12px" />
                      <ConfirmDialog
                        handleConfirm={() => handleConfirmEnrollment(insuranceType)}
                        handleError={handleError}
                        dialogContent={
                          <Text variant="header2" align="left">
                            Are you sure you want to enroll this user in {getInsuranceText(insuranceType)}{' '}
                            insurance?
                          </Text>
                        }
                        confirmLabel="Enroll"
                        width={STANDARD_WRAPPER_WIDTH}
                      >
                        <Button
                          variant="text"
                          size="medium"
                          align="left"
                          onClick={() => handleConfirmEnrollment(insuranceType)}
                        >
                          {insuranceType === InsuranceType.NON_TRUCKING_LIABILITY
                            ? 'Enroll truck'
                            : 'Enroll driver'}
                        </Button>
                      </ConfirmDialog>
                    </>
                  )}
                </FlexBox>
              )
            })}
        </StyledStatus>
      </StyledSubTask>

      <SimpleDialog isOpen={showBeneficiaryInfo} onClose={() => setShowBeneficiaryInfo(false)}>
        <DialogTitle sx={{ paddingTop: '0px' }}>
          <Text variant="header2" align="left">
            Beneficiary for {getUserName(user)}
          </Text>
        </DialogTitle>
        <DialogContent>
          <FlexBox flexDirection="column" gap="12px">
            <FlexBox>
              <StyledLabel variant="header3" align="left" fontColor={Colors.midGray}>
                Name:
              </StyledLabel>
              <StyledValue variant="header3" align="left">
                {currentBeneficiaryInfo ? formattedName(currentBeneficiaryInfo) : ''}
              </StyledValue>
            </FlexBox>

            <FlexBox>
              <StyledLabel variant="header3" align="left" fontColor={Colors.midGray}>
                Relationship:
              </StyledLabel>
              <StyledValue variant="header3" align="left">
                {currentBeneficiaryInfo?.relationship}
              </StyledValue>
            </FlexBox>

            <FlexBox>
              <StyledLabel variant="header3" align="left" fontColor={Colors.midGray}>
                Mobile:
              </StyledLabel>
              <StyledValue variant="header3" align="left">
                {currentBeneficiaryInfo?.mobile ? formatMobile(currentBeneficiaryInfo?.mobile) : ''}
              </StyledValue>
            </FlexBox>

            <FlexBox>
              <StyledLabel variant="header3" align="left" fontColor={Colors.midGray}>
                Email:
              </StyledLabel>
              <StyledValue variant="header3" align="left">
                {currentBeneficiaryInfo?.email}
              </StyledValue>
            </FlexBox>
          </FlexBox>
        </DialogContent>
      </SimpleDialog>
    </>
  )
}

export const TaskListOnboardingInsurance = ({ task, isReadOnly, user }: TaskListOnboardingInsuranceProps) => {
  const ntlTasks = task.subTasks!.filter(
    (t) => t.context?.insurance_type === InsuranceType.NON_TRUCKING_LIABILITY
  )
  const occAccTasks = task.subTasks!.filter(
    (t) => t.context?.insurance_type === InsuranceType.OCCUPATIONAL_ACCIDENT
  )

  const ntlStatus = getStatus(ntlTasks)
  const occAccStatus = getStatus(occAccTasks)

  return (
    <>
      <InsuranceSubTask
        tasks={ntlTasks}
        status={ntlStatus}
        statusDetail={task.statusDetail}
        insuranceType={InsuranceType.NON_TRUCKING_LIABILITY}
        isReadOnly={isReadOnly}
        user={user}
      />
      <InsuranceSubTask
        tasks={occAccTasks}
        status={occAccStatus}
        statusDetail={task.statusDetail}
        insuranceType={InsuranceType.OCCUPATIONAL_ACCIDENT}
        isReadOnly={isReadOnly}
        user={user}
      />
    </>
  )
}

const StyledLabel = styled(Text)`
  width: 100px;
  margin-right: 12px;
`

const StyledValue = styled(Text)`
  overflow-wrap: break-word;
  word-break: break-all;
  hyphens: auto;
`
